import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { booleanBadgeOptions } from 'src/utils/constants';
import { CloudUser, useListCloudUsers } from 'src/features/cloudUsers/api/useListCloudUsers';
import { validatorAccountsMessages } from 'src/features/validatorAccounts/utils/messages';
import { userMessages } from 'src/features/users/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<CloudUser>();

function CloudUsers() {
    const { formatMessage } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...(accountId && { validator_account: accountId }),
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, accountId, queryPaginationOptions, parkingId],
    );

    const { data, isLoading } = useListCloudUsers(payload);

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.name),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(userMessages.active),
                badgeOptions: booleanBadgeOptions,
            },
        }),
    ];

    return (
        <DataTable<CloudUser, any>
            isLoading={isLoading}
            columns={columns}
            data={data}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            semantics={{
                title: formatMessage(validatorAccountsMessages.cloudUsers),
                entryName: formatMessage(validatorAccountsMessages.user).toLowerCase(),
                entryNames: formatMessage(validatorAccountsMessages.users).toLowerCase(),
                placeholder: formatMessage(validatorAccountsMessages.user),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
}

export default CloudUsers;
